import { UAParser } from 'ua-parser-js'

/**
 * The browser-profile-stable device id auth-service keys a web session on (ADR-017 / TED-4200).
 *
 * `localStorage`, not `sessionStorage`, and deliberately so: auth-service keeps a single active session
 * per app and displaces the previous one on sign-in (`createDisplacingActive`). A per-tab id would mean
 * every new tab signing in kicked the other tabs out of their session.
 */
const DEVICE_ID_KEY = 'fluro_device_id'

/**
 * Held in-module as well as in storage so that a browser which refuses `localStorage` (private mode, or
 * site data blocked) still gets one stable id for the life of the page rather than a fresh one per call.
 */
let cachedDeviceId: string | null = null

export function getDeviceId(): string {
  if (cachedDeviceId) return cachedDeviceId

  const stored = readStoredDeviceId()
  if (stored) {
    cachedDeviceId = stored
    return stored
  }

  const generated = generateDeviceId()
  cachedDeviceId = generated
  writeStoredDeviceId(generated)
  return generated
}

/**
 * A human-readable label for the session, shown to staff when they inspect a customer's active sessions.
 * Best-effort: it is optional on the wire, so an unparseable user agent just yields a coarser label.
 */
export function getDeviceLabel(): string {
  const { browser, os } = new UAParser(window.navigator.userAgent).getResult()
  const browserName = browser.name ?? 'Unknown browser'
  return os.name ? `${browserName} on ${os.name}` : browserName
}

/** Clears the stored device id. Only used by tests — a real session keeps its id across sign-outs. */
export function resetDeviceIdForTesting(): void {
  cachedDeviceId = null
  try {
    window.localStorage.removeItem(DEVICE_ID_KEY)
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}

function readStoredDeviceId(): string | null {
  try {
    return window.localStorage.getItem(DEVICE_ID_KEY)
  } catch {
    return null
  }
}

function writeStoredDeviceId(deviceId: string): void {
  try {
    window.localStorage.setItem(DEVICE_ID_KEY, deviceId)
  } catch {
    // Storage unavailable; cachedDeviceId keeps the id stable for this page instead.
  }
}

function generateDeviceId(): string {
  if (typeof window.crypto?.randomUUID === 'function') {
    return window.crypto.randomUUID()
  }
  if (typeof window.crypto?.getRandomValues === 'function') {
    const bytes = window.crypto.getRandomValues(new Uint8Array(16))
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
  }
  // Last resort for a browser with no Web Crypto at all. The id only needs to be unique per browser
  // profile, never unguessable — it is not a credential.
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`
}
